import { MessageLogCallerType, LogLevel, HttpLogMethod, LogRequestStatus, ProjectType, AgentRole, } from '@moderepo/mode-internal-apis';
/**
 * Get the agent's role display name
 */
export const getAgentRoleName = (role) => {
    const names = Object.freeze({
        [AgentRole.ADMIN]: 'agent_role.admin',
        [AgentRole.STAFF]: 'agent_role.staff',
    });
    return names[role];
};
/**
 * The user friendly names for the Message log levels
 */
export const getLogLevelName = (level) => {
    const names = Object.freeze({
        [LogLevel.ERROR]: 'log_level.error',
        [LogLevel.WARN]: 'log_level.warn',
        [LogLevel.INFO]: 'log_level.info',
        [LogLevel.DEBUG]: 'log_level.debug',
    });
    return names[level];
};
export const getLogRequesterTypeName = (callerType) => {
    const names = Object.freeze({
        [MessageLogCallerType.AGENT]: 'log_caller_type.agent',
        [MessageLogCallerType.DEVELOPER]: 'log_caller_type.developer',
        [MessageLogCallerType.DEVICE]: 'log_caller_type.device',
        [MessageLogCallerType.OPERATOR]: 'log_caller_type.operator',
        [MessageLogCallerType.PROJECT]: 'log_caller_type.project',
        [MessageLogCallerType.USER]: 'log_caller_type.user',
    });
    return names[callerType];
};
/**
 * Get the translation key for the given http request method
 */
export const getRequestMethodName = (method) => {
    const names = Object.freeze({
        [HttpLogMethod.GET]: 'http_request_method.get',
        [HttpLogMethod.POST]: 'http_request_method.post',
        [HttpLogMethod.PUT]: 'http_request_method.put',
        [HttpLogMethod.DELETE]: 'http_request_method.delete',
        [HttpLogMethod.PATCH]: 'http_request_method.patch',
        [HttpLogMethod.OPTION]: 'http_request_method.option',
    });
    return names[method];
};
/**
 * User friendly name for log request statuses. These will be used for displaying the status names.
 */
export const getLogRequestStatusName = (status) => {
    const RequestStatusName = Object.freeze({
        [LogRequestStatus.REQUESTED]: 'log_request_status.requested',
        [LogRequestStatus.UPLOADING]: 'log_request_status.uploading',
        [LogRequestStatus.CANCELLED]: 'log_request_status.cancelled',
        [LogRequestStatus.SUCCESS]: 'log_request_status.success',
        [LogRequestStatus.FAILURE]: 'log_request_status.failure',
        [LogRequestStatus.NOLOGS]: 'log_request_status.no_logs',
    });
    return RequestStatusName[status];
};
/**
 * User friendly name for project types. These will be used for displaying the project type.
 */
export const getProjectTypeName = (type) => {
    const ProjectTypeName = Object.freeze({
        [ProjectType.DEFAULT]: 'project_type.default',
        [ProjectType.SENSOR_CLOUD]: 'project_type.sensor_cloud',
        [ProjectType.MOBILITY_CLOUD]: 'project_type.mobility_cloud',
        [ProjectType.MEDICAL_CLOUD]: 'project_type.medical_cloud',
        [ProjectType.ROBOT_CLOUD]: 'project_type.robot_cloud',
    });
    return ProjectTypeName[type] || type;
};
/**
 * The different type of logs
 */
export var LogType;
(function (LogType) {
    LogType["MESSAGES"] = "messages";
    LogType["HTTP_REQUESTS"] = "http_requests";
    LogType["LOG_REQUESTS"] = "log_requests";
})(LogType || (LogType = {}));
/**
 * User friendly name for log types which we can display to the users
 */
export const getLogTypeName = (type) => {
    const names = Object.freeze({
        [LogType.MESSAGES]: 'log_type.message',
        [LogType.HTTP_REQUESTS]: 'log_type.http_request',
        [LogType.LOG_REQUESTS]: 'log_type.log_request',
    });
    return names[type];
};
/**
 * Return the key name for the KV store that we will use to store a gateway's config. We will use device KV store to store Gateway configs
 * and the naming convention for the key is "GATEWAY_CONFIG_${deviceId}""
 * @param deviceId
 */
export const getGatewayConfigKVStoreKey = (deviceId) => {
    return `GATEWAY_CONFIG_${deviceId}`;
};
/**
 * Check if a configParam is hidden. A configParam is hidden if it depends on some other configs' values and those configs DO NOT
 * have the values it required.
 * @param configParam
 * @param configData
 */
export const isConfigParamHidden = (configParam, configData) => {
    // First check if configParams has dependsOnAllParams or dependsOnOneParam props. If it has one of these then we can continue check other things
    // If not then the configParam should not be hidden.
    if ((configParam.dependsOnAllParams && configParam.dependsOnAllParams.length > 0)
        || (configParam.dependsOnOneParam && configParam.dependsOnOneParam.length > 0)) {
        if (configParam.dependsOnAllParams && configParam.dependsOnAllParams.length > 0) {
            // Go through ALL dependencies and find 1 dependency that DOES NOT satisfied. If there is 1 dependency that does not satisfied,
            // then the whole thing failed because this is "dependsOnAllParams" so ALL must satisfied.
            const failedDependency = configParam.dependsOnAllParams.find(({ key: dependedConfigKey, value: dependedConfigValue }) => {
                // if configData is undefined then no need to check anything else, the dependency failed
                if (configData === undefined) {
                    // found a FAILED dependency
                    return true;
                }
                // if there is some config data BUT there isn't config value that match the depended configKey/configValue
                // then the dependency failed as well
                if (configData[dependedConfigKey] !== dependedConfigValue) {
                    // found a FAILED dependency
                    return true;
                }
                return false;
            });
            // If we found a failed dependency then we can return true, this configParam should be hidden.
            if (failedDependency) {
                return true;
            }
        }
        // if we get here, that mean we either didn't need to check configParam.dependOnAll or already satisfied the configParam.dependsOnAllParams
        // So now check if we satisfied the configParam.dependOnOne
        if (configParam.dependsOnOneParam && configParam.dependsOnOneParam.length > 0) {
            // This is different than configParam.dependsOnAllParams. For configParam.dependsOnOneParam, we only need to find 1 dependency that
            // satisfied. Go through ALL dependencies and find 1 dependency that DOES satisfied. If there is 1 dependency that does satisfied,
            // then the whole thing satisfied
            const satisfiedDependency = configParam.dependsOnOneParam.find(({ key: dependedConfigKey, value: dependedConfigValue }) => {
                // if there is some config data for the group AND there the depended on config value matches the depended configKey/configValue
                // then the dependency passed
                if (configData && configData[dependedConfigKey] === dependedConfigValue) {
                    // found a satisfied dependency
                    return true;
                }
                return false;
            });
            // If we can't find even 1 dependency that satisfy then we can return true, the config should be hidden.
            if (!satisfiedDependency) {
                return true;
            }
        }
    }
    // If we get here, that mean the configParams does not depend on any other config OR it depends on some config and all the dependencies are
    // satisfied therefore we can return false, not hidden.
    return false;
};
